import { getFocusStyle, Icon, IconButton, Link, mergeStyleSets, useTheme } from "@fluentui/react";
import * as React from "react";
import { FunctionComponent } from "react";
import { BookingsAndSpaceAvailabilities, Floor, Space, SpaceStatus } from "@graphql";
import ImagePlaceholder from "../../shared/image-placeholder/ImagePlaceholder";
import clsx from "clsx";

interface Props {
  spaceAvailability: BookingsAndSpaceAvailabilities;
  index: number;
  floors?: Floor[];
  onSpaceClick: (id: string) => void;
  onAddSpaceToEvent: (space: Space) => void;
}

const SpaceCard: FunctionComponent<Props> = (props: Props) => {
  const { spaceAvailability, floors, onSpaceClick, onAddSpaceToEvent } = props;
  const theme = useTheme();
  const classNames = mergeStyleSets({
    itemCell: [
      getFocusStyle(theme, { inset: -1 }),
      {
        minHeight: 54,
        padding: 15,
        boxSizing: "border-box",
        borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
        display: "flex",
      },
    ],
    itemContent: {
      marginLeft: 10,
      overflow: "hidden",
      flexGrow: 1,
      paddingLeft: 20,
      cursor: "pointer",
    },
    spaceName: [
      theme.fonts.mediumPlus,
      {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 700,
        fontSize: "14px",
        paddingBottom: 5,
      },
    ],
    subTitle: {
      fontSize: "12px",
      color: "#6c757d",
    },
    available: {
      color: "#5FA911",
      fontSize: "12px",
      fontWeight: 700,
      paddingBottom: 5,
    },
    busy: {
      color: "#ED1008",
      fontSize: "12px",
      fontWeight: 700,
      paddingBottom: 5,
    },
    unavailable: {
      color: "#6c757d",
      fontSize: "12px",
      fontWeight: 700,
      paddingBottom: 5,
    },
    capacityDiv: {
      textAlign: "center",
      backgroundColor: "#F1F1F1",
      padding: 3,
      minWidth: 38,
      color: "#8A8A8A",
    },
    addRemoveIcon: {
      textAlign: "center",
      padding: 3,
      minWidth: 38,
      color: "#8A8A8A",
      marginTop: 8,
    },
    iconSelected: {
      backgroundColor: "#E8F7E7",
    },
    iconUnselected: {
      backgroundColor: "#F1F1F1",
    },
  });

  const onSelect = () => {
    onSpaceClick(spaceAvailability.space._id);
  };

  const setAvailabilityText = (currentAvailability: BookingsAndSpaceAvailabilities) => {
    let availabilityText = "";
    let divClass = classNames.available;

    // const test1 = currentSpace. roomStatus.

    switch (currentAvailability.roomStatus.status) {
      case SpaceStatus.NotBookable:
        {
          availabilityText = "Not Bookable";
          divClass = classNames.busy;
        }
        break;
      case SpaceStatus.AvailableAllDay:
        availabilityText = "Available all day";
        break;
      case SpaceStatus.AvailableUntil:
        availabilityText = `Available until ${currentAvailability.roomStatus.timestamp}`;
        break;
      case SpaceStatus.BusyUntil:
        {
          availabilityText = `Busy until ${currentAvailability.roomStatus.timestamp}`;
          divClass = classNames.busy;
        }
        break;
      case SpaceStatus.BusyAllDay:
        {
          availabilityText = "Busy all day";
          divClass = classNames.busy;
        }
        break;
      case SpaceStatus.Unavailable:
        {
          availabilityText = "Unavailable";
          divClass = classNames.unavailable;
        }
        break;
    }

    return <div className={divClass}>{availabilityText}</div>;
  };

  const getFloorName = (currentAvailability: BookingsAndSpaceAvailabilities) => {
    const floorName = floors?.find((fl) => fl._id == currentAvailability?.space?.floorId)?.name;
    return <div className={classNames.subTitle}>{floorName}</div>;
  };

  const renderIcon = (space: Space) => {
    if (space.addedToOutlook) {
      return (
        <IconButton
          disabled={!space.bookable}
          iconProps={{ iconName: "CheckMark", style: { fontSize: 22 } }}
          style={{ color: "#8DC163" }}
          onClick={() => onAddSpaceToEvent(space)}
        />
      );
    } else if (!space.approvalsRequired) {
      return (
        <IconButton
          disabled={!space.bookable}
          iconProps={{ iconName: "Add", style: { fontSize: 22 } }}
          style={{ color: "#AEAEAE" }}
          onClick={() => onAddSpaceToEvent(space)}
        />
      );
    } else {
      return (
        <Link href="https://ricohspaces.app" target="_blank">
          <Icon iconName="OpenInNewTab" style={{ fontSize: 22, color: "#AEAEAE" }}></Icon>
        </Link>
      );
    }
  };

  return (
    <div className={classNames.itemCell} data-is-focusable={true}>
      <div
        onClick={onSelect}
        style={{
          cursor: "pointer",
        }}
      >
        <ImagePlaceholder src={spaceAvailability.space.image} width={50} height={50} />
      </div>
      <div className={classNames.itemContent} onClick={onSelect}>
        {setAvailabilityText(spaceAvailability)}
        <div className={classNames.spaceName}>{spaceAvailability.space.name}</div>
        {getFloorName(spaceAvailability)}
      </div>
      <div>
        <div className={classNames.capacityDiv}>
          <Icon style={{ paddingRight: 8 }} iconName="People"></Icon>
          {spaceAvailability.space.capacity}
        </div>
        <div
          className={clsx(classNames.addRemoveIcon, {
            [classNames.iconSelected]: spaceAvailability.space.addedToOutlook,
            [classNames.iconUnselected]: !spaceAvailability.space.addedToOutlook,
          })}
        >
          {renderIcon(spaceAvailability.space)}
        </div>
      </div>
    </div>
  );
};

export default SpaceCard;
